import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule, FlxMessageComponent } from '@flowx/angular-ui-toolkit'
import { MessageTemplateConfig } from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxMarkdownPipe } from '../../pipes/flx-markdown.pipe'
import { getBoolExpressionSignal, getDynamicPropSignal, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-message-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxMessageComponent,
    FlxLocalizePipe,
    FlxMarkdownPipe,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './message-wrapper.component.html',
  styleUrl: './message-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxMessageWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<MessageTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  gap = computed(() => {
    const gap = this.style().flexLayout?.fxLayoutGap
    if (gap !== null && gap !== undefined) {
      return `${gap}px`
    }
    return 'var(--flxTheme-message-gap)'
  })

  icons = computed(() => ({
    success: getThemeIcon(ThemeComponent.Message, 'successIcon'),
    warning: getThemeIcon(ThemeComponent.Message, 'warningIcon'),
    error: getThemeIcon(ThemeComponent.Message, 'errorIcon'),
    info: getThemeIcon(ThemeComponent.Message, 'infoIcon'),
  }))

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  message: Signal<string>

  ariaHidden: Signal<boolean>

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { message, hide } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.ariaHidden = computed(() => this.flowxProps().accessibility?.decorative)
      this.message = getDynamicPropSignal(message, this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
